/**
 * Copy for the legal and support routes.
 *
 * Rewritten from the original bullet skeleton, which read as a table of
 * contents rather than a policy -- "Cancellation policy" and "Refund
 * conditions" were list ITEMS with no text behind them. Razorpay reads these
 * pages during merchant onboarding and Play reads the privacy policy, so
 * headings with nothing under them fail both.
 *
 * Three things were actively wrong and are gone:
 *
 *  - "End-to-end encryption" under Data Security. This product does not do
 *    end-to-end encryption -- the server reads every record it stores, which is
 *    what lets it send reminders and compute a ledger. Play cross-checks the
 *    Data Safety form against the policy, so an unsupportable security claim is
 *    a rejection risk rather than marketing puff.
 *  - `connect.floc@gmail.com` as the contact and account-deletion route: a
 *    different brand, on a free mailbox.
 *  - "support is available around the clock" / "24/7 support". A promise the
 *    team cannot keep is worse than a stated response window.
 *
 * Entity facts come from company.js so they are written once.
 */

import { COMPANY } from "./company";

const { product, legalName, address, supportEmail, grievanceOfficer, trialDays } = COMPANY;

/** The date these documents last changed. Update it when you edit them. */
const UPDATED = "October 2026";

export const ABOUT = {
  title: `About ${product}`,
  lead: "Software for running a gym.",
  sections: [
    {
      heading: "The app",
      body: [
        `${product} is built for gym owners and trainers: members, plans, payments, attendance and reminders in one place, on a phone or a desktop browser.`,
      ],
      items: [
        "Membership and plan management",
        "Payments, receipts and a running ledger",
        "Attendance and check-in",
        "Payment reminders, sent by you or automatically",
      ],
    },
    {
      heading: "Who we are",
      body: [
        `${product} is operated by ${legalName}, ${address}.`,
        `Questions about the product or this site: ${supportEmail}.`,
      ],
    },
  ],
};

export const PRIVACY = {
  title: "Privacy Policy",
  updated: UPDATED,
  lead: `How ${legalName} handles the information you and your members give us.`,
  sections: [
    {
      heading: "Who this covers",
      body: [
        `This policy covers the ${product} club console (on the web, on Android and on iPhone) and the ${product} member app.`,
        "A gym owner is our customer. The members of that gym are our customer's contacts, and we process their details on the gym's behalf and under its instructions.",
      ],
    },
    {
      heading: "What we collect",
      items: [
        "Account details of the gym owner: name, mobile number, email address, and the club's name, address, city, website, opening hours, facilities and the state used for GST",
        "Trainer details entered by the owner: name, mobile number, email address and role",
        "Member records entered by the gym: name, mobile number, email address, gender, date of birth, height, weight, photo, goal, plan, batch, joining and expiry dates, amounts paid and due, payments and attendance, the workouts assigned to the member, and whether and when the member agreed to receive WhatsApp messages",
        "Fitness data entered in the member app: height, weight, goals, workout logs and meal logs",
        "Text the gym writes in the app, such as message templates, answers to member questions, and feedback to us",
        "Images uploaded by a user, such as a member photo, the club's logo and photos, or an exercise picture",
        "Camera and photos: the app asks for access to the camera or photo library only when you choose to add a picture, and uses it only to upload the picture you pick",
        "Location: the gym's coordinates. The console reads your phone's location once, only when you tap the button that fills them in, and never in the background. The coordinates are saved with the club's profile",
        "The hours you usually use the app, to send reminders when you are likely to see them. We keep a count for each hour of the day, not a log of every visit",
        "Messaging records: the WhatsApp messages sent through the service on a gym's behalf, and messages received on our WhatsApp number, with the number, the text, the time and the delivery status",
        "Technical and usage data: device type, operating system, app version, IP address, the screens opened and actions taken, and crash and error reports",
      ],
    },
    {
      heading: "Why we collect it",
      items: [
        "To run the service you signed up for: recording members, payments and attendance",
        "To send the messages you ask us to send, including payment reminders and one-time sign-in codes",
        "To take payment for a subscription and issue the invoice for it",
        "To send you push notifications about your club, such as a new join request or reminders waiting to be sent",
        "To send reminders at an hour when you are likely to see them",
        "To keep a record that WhatsApp messages were sent, and that members agreed to them",
        "To understand how the product is used and to improve it (product analytics)",
        "To diagnose crashes and errors, and to keep the service working",
      ],
      body: [
        "We do not sell personal information, and we do not use anything stored in the app, about your club, your members or your account, for advertising. Our marketing website (varzishmate.com) uses the Meta Pixel and the Google tag to measure our own ads; both are described with the other providers below.",
      ],
    },
    {
      heading: "Who we share it with",
      body: [
        "Only the service providers needed to run the product, and only with what each one needs. Some of them store and process data outside India, including in the European Union and the United States; where that happens we rely on the provider's own contractual safeguards for the transfer.",
        "Google user data: when you choose Sign in with Google we receive only your email address, name and Google account id, and use them only to create and recognise your account. We do not use them for advertising, we do not sell them, we do not share them with anyone except the providers listed here who run the service, and no person reads them except as needed for support or security. Our use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements.",
        "A club's public profile is shown to signed-in member-app users who browse or search for gyms, including gyms nearby: its name, description, opening hours, address, city, phone number, website, fees, photos, rating, facilities and map coordinates. Member records are never part of it.",
        "Push notifications travel through the providers below. The text of a notification can include a person's name, for example \"Aditi asked to join.\" when someone asks to join your club.",
        "Each provider, and what it receives:",
      ],
      items: [
        "Razorpay — payment processing. Card and bank details are entered on Razorpay's own checkout and never reach our servers",
        "Meta Platforms Ireland — sending WhatsApp messages, including sign-in codes, reminders and billing links. For automatic payment reminders it receives the member's name and phone number, the amount due and the relevant dates, so that it can deliver the message",
        "Google LLC — the Google tag (Google Ads), on our marketing website (varzishmate.com) only. When you arrive from a Google ad and then tap a button to get the app, Google receives that visit and tap, the address of the page, and what your browser sends with every request (IP address, browser and device type). We use it to count how many ad clicks lead to someone trying the app. The tag is not in the Android app or the web console and receives nothing about your club or your members. You can limit it in your Google ad settings (adssettings.google.com)",
        "Resend — sending email",
        "Google — Sign in with Google, which gives us your email address, name and Google account id",
        "Expo — delivering push notifications and app updates. It receives the device's push token, the app and device version, and the text of each notification it delivers",
        "Apple — delivering push notifications on iPhone (Apple Push Notification service)",
        "Google Firebase — delivering push notifications on Android (Firebase Cloud Messaging), and storing images uploaded to the member app before they moved to Cloudinary",
        "Cloudinary — storing and serving uploaded images",
        "PostHog — product analytics, hosted in the European Union. We send pseudonymous identifiers, your role, your plan, the club's city, a size band for the number of members (not the exact number), and usage events and screen names. We do not send your name, email address or phone number, and we do not send any member's personal information",
        "Meta Platforms Ireland — the Meta Pixel, on our marketing website (varzishmate.com) only. When you visit it, Meta receives the pages you open, whether you tap a button to get the app, the address of the page that sent you there, and what your browser sends with every request (IP address, browser and device type). Meta may link this to your Facebook or Instagram account. We use it to measure our own ads and to show them to people likely to want the app. The Pixel is not in the Android app or the web console and receives nothing about your club or your members. You can limit how Meta uses it in your Facebook or Instagram ad settings",
        "MongoDB Atlas and Render — database and application hosting",
        "Sentry — crash and error reporting. It receives the error and where in the code it happened, the app version, the device type and operating system, and the steps leading up to the error: screens opened, taps, and the addresses of the requests the app made, with any query string removed. We do not record your screen, and Sentry's option to collect personal information automatically is off",
        "A government authority, where the law requires it",
      ],
    },
    {
      heading: "Google user data",
      body: [
        `${product} offers Sign in with Google to gym owners and trainers on the web console and the Android app. It is not offered on iPhone, where you sign in with a code sent to your WhatsApp number or your email address. This section describes how ${legalName} handles the data Google gives us when you use it.`,
      ],
      items: [
        "What we access: your Google account's email address, name and account id, through the basic email and profile sign-in scopes. We do not request access to your Gmail, Drive, Calendar, contacts or any other Google service",
        "How we use it: only to create your account, recognise you when you sign in again, and send you account and billing emails. It is not used for advertising, profiling or analytics",
        "Who we share it with: no one, apart from the service providers listed above that run the product on our behalf, and only where the service needs it. We do not sell it and we do not transfer it to data brokers or advertising platforms",
        "How we store and protect it: in our production database, encrypted in transit with TLS, with access limited to the people who operate the service",
        "How long we keep it and how to delete it: for as long as the account is open. Delete your club from the console (Club → Settings → Delete club), or write to the support address above, and we remove it within 30 days. You can also revoke our access at any time at myaccount.google.com/permissions",
      ],
    },
    {
      heading: "Security",
      body: [
        "Traffic between the apps and our servers is encrypted in transit with TLS. Access to production data is restricted to the people who operate the service. Passwords are not stored in readable form, and sign-in codes are short-lived.",
        "We do not offer end-to-end encryption. Our servers can read the records you store, because that is what allows the service to send reminders, compute a ledger and produce a receipt. Anyone claiming otherwise about software of this kind is worth a second look.",
      ],
    },
    {
      heading: "How long we keep it",
      items: [
        "Account and member records are kept while the account is open",
        "When a gym owner deletes a club, the club is hidden at once and can be restored for 7 days. After that we remove the club, its members, plans, payments, attendance, reminders and reminder history, check-in codes, notifications, feedback, reviews and images within 30 days",
        "When a member deletes their member-app account, we remove their profile, workout and diet logs, and images within 30 days. The gym's own record of that person as a member (dates, plan, payments, attendance) belongs to the gym and stays with it; ask the gym to remove it",
        "Records of WhatsApp messages (the number, the text, the time and the delivery status) are not removed with the club or the member. We keep them as evidence that a message was sent and that the recipient had agreed to it, and for audit",
        "Invoices, payment records and tax records are kept for as long as Indian tax law requires, which is longer than 30 days and applies even after deletion",
      ],
    },
    {
      heading: "Your rights, and how to use them",
      body: [
        "You can ask us for a copy of your data, ask us to correct it, or ask us to delete it.",
        `Gym owners: delete your club from the console at any time, under Club → Settings → Delete club. We email a code to the club's address to confirm it.`,
        "Members: delete your account from the member app, under Profile → Settings → Delete Account.",
        `Or write to ${supportEmail} from the email address on the account, and we will do it for you.`,
      ],
    },
    {
      heading: "Children",
      body: [
        "The service is not directed at children under 13, and we do not knowingly collect their information. A gym that records a minor as a member is responsible for having the guardian's consent.",
      ],
    },
    {
      heading: "Grievance officer",
      body: [
        "Under the Digital Personal Data Protection Act, 2023, complaints about how your data is handled go to a named officer:",
        `${grievanceOfficer.name} — ${grievanceOfficer.email}`,
        "We aim to acknowledge within 48 hours and resolve within 30 days.",
      ],
    },
    {
      heading: "Changes",
      body: [
        "If this policy changes materially we will update the date at the top and, where the change affects you, tell you in the app.",
      ],
    },
  ],
};

export const TERMS = {
  title: "Terms of Service",
  updated: UPDATED,
  lead: `The agreement between you and ${legalName} for using ${product}.`,
  sections: [
    {
      heading: "The agreement",
      body: [
        `By creating an account you agree to these terms. If you are accepting on behalf of a gym or company, you confirm you are allowed to bind it.`,
      ],
    },
    {
      heading: "Your account",
      items: [
        "Give accurate details, and keep them current",
        "Keep your sign-in secure — anyone with your mobile number and access to its messages can sign in",
        "You are responsible for what your staff accounts do",
        "Have the right to enter the member details you enter",
      ],
    },
    {
      heading: "Automatic WhatsApp reminders",
      body: [
        "A club can choose, for each type of reminder, whether to send it by hand from its own WhatsApp or have the service send it automatically. If you turn on automatic reminders, you confirm that your members have agreed to receive WhatsApp messages about their membership and payments, and you are responsible for that consent.",
        `Automatic reminders are sent from the ${product} WhatsApp Business number using WhatsApp message templates. They are subject to a monthly allowance that depends on your plan, and to WhatsApp's own terms and policies. A reminder that cannot be sent automatically is left for you to send by hand.`,
      ],
    },
    {
      heading: "Subscriptions and billing",
      body: [
        `Every new club starts with a ${trialDays}-day free trial. No card is needed to begin.`,
        "After the trial, continued use of the console requires a paid plan. Plans are sold as fixed terms of 3, 6 or 12 months and are paid upfront.",
        "A term does not renew automatically. When it ends you choose whether to buy another.",
        "Paying during a trial or before a term ends adds the new term on top of the time you already have. You never lose days by paying early.",
        "Prices are shown before GST. GST at 18% is added at checkout, and a GST invoice is issued for every payment.",
        "Razorpay, our payment processor, may add its own convenience fee for the payment method you choose. It is shown to you before you pay, is collected by Razorpay, and is not part of our invoice.",
      ],
    },
    {
      heading: "If you stop paying",
      body: [
        "When a term ends without renewal the console becomes read-only: you keep access to your data and can export it, but you cannot add members, record payments or send reminders until you renew.",
        "We do not delete your data because a payment lapsed.",
      ],
    },
    {
      heading: "Cancellation and refunds",
      body: [
        "You can cancel at any time by not renewing. Refunds are covered in full on the refund policy page.",
      ],
    },
    {
      heading: "Acceptable use",
      items: [
        "Do not use the service to send unsolicited marketing to people who have not agreed to hear from you",
        "Do not attempt to access another club's data",
        "Do not resell or sublicense the service without our written agreement",
        "Do not upload content you do not have the rights to",
      ],
    },
    {
      heading: "Availability",
      body: [
        "We work to keep the service running but do not promise uninterrupted availability. Maintenance, provider outages and events outside our control can interrupt it.",
      ],
    },
    {
      heading: "Ending the agreement",
      body: [
        "You can close your account at any time from the app. We may suspend or close an account that breaches these terms, that is used unlawfully, or where payment is not made — after telling you, except where the breach makes that unsafe.",
      ],
    },
    {
      heading: "Liability",
      body: [
        "To the extent the law allows, our total liability for any claim is limited to what you paid us in the twelve months before it arose. We are not liable for lost profit or lost business.",
        "Nothing here limits liability that cannot be limited by law.",
      ],
    },
    {
      heading: "Governing law",
      body: [
        "These terms are governed by the laws of India, and the courts of India have jurisdiction over any dispute.",
      ],
    },
    {
      heading: "Contact",
      body: [`Questions about these terms: ${supportEmail}.`],
    },
  ],
};

export const REFUNDS = {
  title: "Refund and Cancellation Policy",
  updated: UPDATED,
  lead: "What happens when you cancel, and when money comes back.",
  sections: [
    {
      heading: "Cancelling",
      body: [
        "There is nothing to cancel in advance. Plans are fixed terms paid upfront and do not renew automatically, so choosing not to buy another term is how you cancel.",
        "Your console stays fully usable until the term you paid for ends.",
      ],
    },
    {
      heading: "The free trial",
      body: [
        `Every club gets ${trialDays} days free with no card. If you decide the product is not for you during the trial, do nothing — you are never charged, and there is nothing to refund.`,
      ],
    },
    {
      heading: "Refunds on a paid term",
      items: [
        "Within 7 days of a payment, if you have not used the console in that period beyond signing in, we refund it in full",
        "A duplicate charge, or a charge for a term you already hold, is refunded in full whenever it is reported",
        "A payment that was taken but did not activate a term is refunded in full, or the term is activated, whichever you prefer",
        "Beyond 7 days, a paid term is not refundable pro-rata — the plan is a fixed term, and its price reflects that",
      ],
    },
    {
      heading: "If we end your account",
      body: [
        "If we close your account for a reason that is not a breach of the terms by you, we refund the unused part of the term you paid for.",
      ],
    },
    {
      heading: "How to ask, and how long it takes",
      body: [
        `Write to ${supportEmail} from the email or mobile number on the account, with the invoice number. We respond within 3 working days.`,
        "An approved refund is returned through Razorpay to the original payment method. Razorpay and your bank typically take 5 to 7 working days after we issue it.",
      ],
    },
  ],
};

export const DELETE_ACCOUNT = {
  title: "Delete your account",
  updated: UPDATED,
  lead: `How to delete your ${product} or Lfit account, and what happens to your data when you do.`,
  sections: [
    {
      heading: "Which account you have",
      items: [
        `Gym owner or trainer: you use the ${product} app or the web console to run a club. Your account is the club.`,
        "Member: you use the Lfit member app to log workouts and meals. Your account is your own profile.",
      ],
    },
    {
      heading: "Delete a member account in the Lfit app",
      body: [
        "Open the Lfit app and follow these steps:",
      ],
      items: [
        "Go to Profile, then Settings",
        "Tap Delete Account",
        "Confirm when asked. This cannot be undone",
      ],
    },
    {
      heading: "What is removed when a member deletes their account",
      body: [
        "You are signed out and your account is deleted straight away. We remove:",
      ],
      items: [
        "Your profile and sign-in details",
        "Your workout logs, workout history, saved workout templates and custom exercises",
        "Your meal and diet logs",
        "Your notifications",
        "Images you uploaded, within 30 days",
      ],
    },
    {
      heading: "Delete a club (gym owners and trainers)",
      body: [
        `Open the ${product} app or the web console and follow these steps. You can export your members and payments first, from the same screen.`,
      ],
      items: [
        "Go to Club, then Settings",
        "Tap Delete club",
        "Enter the 6-digit code we email to the club's address, to confirm it is you",
      ],
    },
    {
      heading: "What is removed when a club is deleted",
      body: [
        "The club is hidden at once, and you can restore it for 7 days by signing in again. After that we remove, within 30 days:",
      ],
      items: [
        "The club, its details and its images",
        "Members, plans, attendance and check-in codes",
        "Payments recorded in the club, reminders and reminder history",
        "Notifications, feedback and reviews",
      ],
    },
    {
      heading: "If you cannot open the app",
      body: [
        `Write to ${supportEmail} from the email address on the account. Say whether it is a member account or a club, and give the name and mobile number on it. We reply within one working day and complete the deletion within 30 days.`,
      ],
    },
    {
      heading: "What we keep, and for how long",
      items: [
        "Invoices, payment records and tax records, for as long as Indian tax law requires. This is longer than 30 days and applies even after deletion",
        "Records of WhatsApp messages (the number, the text, the time and the delivery status), as evidence that a message was sent and that the recipient had agreed to it",
        "A gym's own record of a person as a member (dates, plan, payments, attendance) belongs to the gym. Deleting a Lfit account does not remove it. Ask the gym to remove it",
      ],
      body: [
        "Everything else is deleted. See the Privacy Policy for the full detail.",
      ],
    },
  ],
};

export const HELP = {
  title: "Help",
  lead: "Common topics, and how to reach a person.",
  topics: [
    {
      heading: "Getting started",
      items: [
        "Creating your club and signing in",
        "Adding members and plans",
        "Recording a payment",
        "Adding trainers and staff",
      ],
    },
    {
      heading: "Day to day",
      items: [
        "Attendance and check-in",
        "Payment reminders",
        "Receipts and statements",
      ],
    },
    {
      heading: "Billing",
      items: [
        "Your plan and renewal date",
        "Invoices and GST",
        "Changing where billing emails go",
        "Refunds and cancellation",
      ],
    },
  ],
  contact: {
    email: supportEmail,
    note: "We answer within one working day.",
  },
};
